import { useState } from 'react';
import { darkTextDim, darkDangerText, addButtonStyle, darkPanelStyle } from '@/shared/dark-inline-styles';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { usePostTrackingUpdate } from '@/services/orders';
import type { TrackingEvent, TrackingStatus } from '@/models/order';

const STATUS_COLOR: Record<TrackingStatus, string> = {
    pending: '#9aa0a6',
    in_transit: '#5b9dd9',
    out_for_delivery: '#d9a55b',
    delivered: '#5bd97f',
    exception: '#d95b5b',
};

const STATUS_LABEL: Record<TrackingStatus, string> = {
    pending: 'Label created',
    in_transit: 'In transit',
    out_for_delivery: 'Out for delivery',
    delivered: 'Delivered',
    exception: 'Exception',
};

interface Props {
    events?: TrackingEvent[];
    statusDetail?: string;
    lastCheckedAt?: string;
    lastError?: string;
    /** Whether the order has a tracking code at all — changes the empty copy. */
    hasTrackingCode: boolean;
    /**
     * The order to post manual updates against. Omit to render read-only.
     *
     * Optional so the component can still be dropped somewhere that only
     * DISPLAYS a timeline without offering to edit it.
     */
    orderId?: string;
}

/**
 * Carrier scan history for one parcel, for STAFF.
 *
 * ─── Why the empty states are the important part ──────────────────────────
 *
 * Auto-tracking is dormant until a carrier provider has credentials, so "no
 * events" is the normal case today and will stay so for a while. A panel that
 * rendered nothing would read as broken; a panel that says "auto-tracking is not
 * switched on in this environment" tells an admin exactly where they stand.
 *
 * ─── Why the poll error is shown here and nowhere else ────────────────────
 *
 * `trackingLastError` is staff-only and deliberately absent from the customer's
 * order payload. A parcel that has silently stopped updating is
 * indistinguishable from one that is not moving, and an admin has to tell those
 * apart before answering anybody — but a customer reading "FedEx: unknown
 * tracking error" would reasonably conclude their board is lost.
 */
export const TrackingTimeline = ({ events, statusDetail, lastCheckedAt, lastError, hasTrackingCode, orderId }: Props) => {
    const list = events ?? [];

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: darkTextDim, marginBottom: 8 }}>Carrier updates</div>

            {statusDetail && <div style={{ fontSize: 14, marginBottom: 6 }}>{statusDetail}</div>}

            {lastError && (
                <div style={{ color: darkDangerText, fontSize: 12, marginBottom: 8 }}>
                    Last check failed: {lastError}
                </div>
            )}

            {list.length === 0 && (
                <div style={{ fontSize: 12, color: darkTextDim }}>
                    {!hasTrackingCode
                        ? 'No tracking code on file yet, so there is nothing to poll.'
                        : lastCheckedAt
                          ? 'The carrier has reported no scans for this code yet.'
                          : 'Automatic carrier tracking is not switched on in this environment — this parcel is tracked manually.'}
                </div>
            )}

            {list.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {list.map((event, index) => (
                        <li
                            key={`${event.at}-${index}`}
                            style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: index === list.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)' }}
                        >
                            <span
                                aria-hidden
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: STATUS_COLOR[event.status] ?? STATUS_COLOR.in_transit,
                                    marginTop: 6,
                                    flexShrink: 0,
                                }}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13 }}>{event.description}</div>
                                <div style={{ fontSize: 11, color: darkTextDim, marginTop: 2 }}>
                                    {new Date(event.at).toLocaleString()}
                                    {event.location ? ` · ${event.location}` : ''}
                                    {` · ${STATUS_LABEL[event.status] ?? event.status}`}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {lastCheckedAt && (
                <div style={{ fontSize: 11, color: darkTextDim, marginTop: 8 }}>Last checked {new Date(lastCheckedAt).toLocaleString()}</div>
            )}

            {orderId && <ManualUpdateForm orderId={orderId} />}
        </div>
    );
};

/**
 * Post a tracking update by hand.
 *
 * ─── Why staff need this at all ───────────────────────────────────────────
 *
 * Carrier auto-tracking is dormant until FedEx credentials are provisioned, and
 * which carriers the shipping partner uses is still an open question. Until both
 * are settled, a staff member reading the carrier's own website is the only
 * source of "where is it now" — and without this form they could tell a customer
 * "it shipped" and "it arrived" and nothing in between. A parcel held in customs
 * left them a choice between saying nothing and saying nothing.
 *
 * ─── Notifying is opt-in per update ───────────────────────────────────────
 *
 * Unchecked by default. A parcel produces many small movements and most do not
 * deserve an interruption; the admin decides which one does. That is the same
 * judgement the automated poll encodes by only notifying on delivery and
 * exceptions.
 */
const ManualUpdateForm = ({ orderId }: { orderId: string }) => {
    const [detail, setDetail] = useState('');
    const [location, setLocation] = useState('');
    const [status, setStatus] = useState<TrackingStatus>('in_transit');
    const [notify, setNotify] = useState(false);
    const [error, setError] = useState('');
    const post = usePostTrackingUpdate();

    const submit = async () => {
        setError('');
        const statusDetail = detail.trim();
        if (!statusDetail) {
            setError('Say what the carrier is reporting.');
            return;
        }
        const result = await post.mutateAsync({ orderId, statusDetail, status, location: location.trim() || undefined, notify });
        // `callApi` NEVER throws on an HTTP error — it resolves with
        // `status: 'error'`. Without this check the form would clear itself and
        // report success on a failed save.
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Could not save that update.');
            return;
        }
        setDetail('');
        setLocation('');
        setNotify(false);
    };

    return (
        <div style={{ ...darkPanelStyle, marginTop: 14, textAlign: 'left' }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: darkTextDim, marginBottom: 8 }}>Post an update</div>

            <MaterialTextField label="What the carrier is reporting" value={detail} onChange={(e) => setDetail(e.target.value)} />
            <div style={{ display: 'flex', gap: 10 }}>
                <MaterialTextField label="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} />
                <MaterialTextField select label="State" value={status} onChange={(e) => setStatus(e.target.value as TrackingStatus)}>
                    {(Object.keys(STATUS_LABEL) as TrackingStatus[]).map((key) => (
                        <MaterialFormOption key={key} value={key}>
                            {STATUS_LABEL[key]}
                        </MaterialFormOption>
                    ))}
                </MaterialTextField>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 0', fontSize: 13 }}>
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                Tell the customer (on whichever channels are switched on for this order)
            </label>
            {notify && status === 'exception' && (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: darkTextDim }}>
                    Sends the &ldquo;needs attention&rdquo; copy, which asks them to reply &mdash; use it when they have to act.
                </p>
            )}

            {error && <p style={{ margin: '8px 0 0', fontSize: 12, color: darkDangerText }}>{error}</p>}

            <button type="button" style={{ ...addButtonStyle, marginTop: 12 }} onClick={submit} disabled={post.isPending}>
                {post.isPending ? 'Saving…' : 'Save update'}
            </button>
        </div>
    );
};

export default TrackingTimeline;
